// В.2.b: утилита лечения юнитов / Героев Лив Лайф + событие healed.
//
// Все хил-ауры и хил-эффекты Лив Лайф ОБЯЗАНЫ ходить через healUnit, чтобы
// триггеры on_heal (Ошибка эксперимента, Бьорк в В.2.h) срабатывали единообразно.
//
// API:
//   healUnit(state, target, amount, source)
//     - target — Card-юнит на поле, ИЛИ Card-Герой (state.players[idx].hero).
//       Любой персонаж с current.hp / base.hp работает.
//     - amount — целое >= 0. amount<=0 → no-op (actualAmount=0, overflow=0,
//       событие healed НЕ эмитится).
//     - source — Card | string | null. Используется для лога и фильтрации
//       триггеров. Если Card — берётся source.name_ru; если string — как есть.
//     - Не лечит мертвую цель (current.hp <= 0 у юнита; для Героя hp может
//       быть >0 — тогда лечим).
//     - Возвращает { actualAmount, overflow }.
//     - Если actualAmount === 0 — событие healed НЕ эмитится. Это важно для
//       триггера Ошибки эксперимента: без реального хила нет +atk.
//
//   registerOnHealTrigger(cardId, fn)
//     - Регистрирует обработчик. fn(state, healEvent) вызывается когда
//       healEvent.target — карта c id === cardId. Обработчик сам решает,
//       подходит ли событие (например, Бьорк смотрит на любого союзника).
//       Здесь по умолчанию фильтрация по cardId самой ЦЕЛИ — этого хватает
//       для Ошибки эксперимента в В.2.b. Карты типа Бьорка в В.2.h будут
//       регистрироваться через отдельный API (в этом шаге не нужно).
//
//   runOnHealTriggers(state, healEvent)
//     - Идёт по обоим игрокам, по живым юнитам / Героям; для каждого, чей
//       cardId совпал с зарегистрированным cardId-фильтром, и кто является
//       healEvent.target, вызывает зарегистрированную fn.
//     - Молчание блокирует триггер: если на карте-владельце триггера висит
//       Молчание (current.silenced или silenceSources не пустое) — fn не зовётся.
//
// healEvent: { target, source, amount: actualAmount, overflow }.

import { shortHeroName } from '../../../data/heroes.js';

// cardId -> fn(state, healEvent). Срабатывает, когда healEvent.target.id === cardId.
const _onHealTriggers = new Map();
// В.2.g: side-aware observer'ы (Бьорк) — fn(state, observer, healEvent).
// Срабатывает, когда healEvent.target.ownerId === observer.ownerId, observer
// жив и не под Молчанием. observer ищется по cardId среди юнитов и Героя
// стороны target'а.
const _onAllyHealedObservers = new Map();
// В.2.g: heal-blocker'ы (Бессмертный) — fn(state, target, source) → true
// разрешено, false — заблокировать. Идут ДО применения хила.
const _healBlockers = new Map();
// В.2.g: post-heal observer'ы (Зейн overshield) — fn(state, observer, healEvent).
// Срабатывает после успешного хила любого союзника observer'а.
const _onHealOverflowObservers = new Map();

export function registerOnHealTrigger(cardId, fn) {
  if (typeof cardId !== 'string' || typeof fn !== 'function') return;
  _onHealTriggers.set(cardId, fn);
}

// В.2.g: на стороне любого союзника произошёл хил.
export function registerOnAllyHealedObserver(cardId, fn) {
  if (typeof cardId !== 'string' || typeof fn !== 'function') return;
  _onAllyHealedObservers.set(cardId, fn);
}

// В.2.g: pre-check блокировки хила для конкретной цели (target.id).
//   fn(state, target, source) → true (allow) | false (block).
export function registerHealBlocker(cardId, fn) {
  if (typeof cardId !== 'string' || typeof fn !== 'function') return;
  _healBlockers.set(cardId, fn);
}

// В.2.g: post-heal observer (Зейн overshield).
export function registerOnHealOverflowObserver(cardId, fn) {
  if (typeof cardId !== 'string' || typeof fn !== 'function') return;
  _onHealOverflowObservers.set(cardId, fn);
}

// Только для тестов: очистить все триггеры.
export function _resetOnHealTriggersForTests() {
  _onHealTriggers.clear();
  _onAllyHealedObservers.clear();
  _healBlockers.clear();
  _onHealOverflowObservers.clear();
}

// Применить лечение и эмитить событие. Возвращает { actualAmount, overflow }.
//
// В.2.h-rebalance: добавлен опциональный options.allowOverflow.
//   - false (default): cap по max_hp, как раньше.
//   - true: current_hp += amount БЕЗ cap'а. actualAmount === amount, overflow=0.
//     Используется активкой Бьорка («+2 ХП всем своим, MOGUT превышать max_hp»).
//     Эта избыточная часть НЕ возвращается по таймеру / в начале хода — она
//     остаётся как часть текущего HP пока её не «съест» урон.
export function healUnit(state, target, amount, source = null, options = {}) {
  const allowOverflow = options?.allowOverflow === true;
  const ZERO = { actualAmount: 0, overflow: Math.max(0, amount | 0) };
  if (!target) return ZERO;
  if (typeof amount !== 'number' || amount <= 0) return ZERO;

  // Жив ли target? Для юнита проверяем isAlive(); для Героя — hp > 0.
  const isUnit = target.type === 'unit';
  const isHero = target.type === 'hero';
  if (isUnit && (!target.isAlive || !target.isAlive())) return ZERO;
  if (isHero && (target.hp == null || target.hp <= 0)) return ZERO;
  if (!isUnit && !isHero) return ZERO;

  // В.2.g: heal-blocker. Бессмертный блокирует хилы кроме Донора и своей
  // активки. Молчание blocker-носителя — пассивка отключена (внутри fn).
  const blockerFn = _healBlockers.get(target.id);
  if (blockerFn) {
    const allowed = blockerFn(state, target, source);
    if (allowed === false) {
      const ownerName = state?.players?.[target.ownerId]?.name || '?';
      const targetName = isHero ? shortHeroName(target) : (target.name_ru || target.id);
      state?.log?.add?.(
        `🚫 ${targetName} [${ownerName}]: попытка хила от ${sourceLabel(source)} заблокирована`
      );
      return ZERO;
    }
  }

  const before = target.current?.hp ?? target.hp ?? 0;
  const max = target.base?.hp ?? target.maxHp ?? before;
  // В.2.h-rebalance: с allowOverflow cap снимается, лечим даже если cur>=max.
  if (!allowOverflow && before >= max) {
    return { actualAmount: 0, overflow: amount };
  }
  const after = allowOverflow ? (before + amount) : Math.min(before + amount, max);
  const actualAmount = after - before;
  const overflow = allowOverflow ? 0 : (amount - actualAmount);
  if (target.current) target.current.hp = after;
  else target.hp = after;

  // Лог.
  const sourceName = sourceLabel(source);
  const ownerName = state?.players?.[target.ownerId]?.name || '?';
  const targetName = isHero ? shortHeroName(target) : (target.name_ru || target.id);
  state?.log?.add?.(
    `✚ ${targetName} [${ownerName}]: +${actualAmount} ХП от ${sourceName}` +
    (overflow > 0 ? ` (излишек ${overflow})` : '')
  );

  // Эмитить событие healed (только если actualAmount > 0).
  const healEvent = { target, source, amount: actualAmount, overflow };
  runOnHealTriggers(state, healEvent);
  // В.2.g: side-observer'ы (Бьорк) — после стандартных триггеров.
  runOnAllyHealedObservers(state, healEvent);
  // В.2.g: overflow-observer'ы (Зейн overshield) — только если overflow > 0.
  if (overflow > 0) runOnHealOverflowObservers(state, healEvent);

  return { actualAmount, overflow };
}

// Запустить триггеры on_heal для одного события.
export function runOnHealTriggers(state, healEvent) {
  if (!state || !healEvent || !healEvent.target) return;
  if (_onHealTriggers.size === 0) return;
  // Сейчас — простая модель: триггер карты X срабатывает, если target имеет
  // тот же id (т.е. Ошибка эксперимента ловит лечение самой себя).
  const targetId = healEvent.target.id;
  const fn = _onHealTriggers.get(targetId);
  if (!fn) return;
  // Триггер живёт «на самой цели». Молчание цели блокирует её собственные
  // триггеры (диздок 6.7).
  const owner = healEvent.target;
  if (owner.current?.silenced) return;
  if (owner.silenceSources && owner.silenceSources.size > 0) return;
  // Юнит должен быть жив.
  if (owner.type === 'unit' && (!owner.isAlive || !owner.isAlive())) return;
  if (owner.type === 'hero' && (owner.hp == null || owner.hp <= 0)) return;
  try {
    fn(state, healEvent);
  } catch (e) {
    console.warn(`[Корпы] триггер on_heal (${targetId}) упал:`, e);
  }
}

// В.2.g: side-observer dispatcher (Бьорк).
// Идём по обоим игрокам; для Героя+юнитов owner-стороны проверяем
// зарегистрированный observer по их id; если он есть и observer жив и не
// под Молчанием — вызываем fn(state, observer, healEvent).
function runOnAllyHealedObservers(state, healEvent) {
  if (!state || !healEvent?.target) return;
  if (_onAllyHealedObservers.size === 0) return;
  const ownerIdx = healEvent.target.ownerId;
  if (ownerIdx == null) return;
  const owner = state.players?.[ownerIdx];
  if (!owner) return;
  const candidates = [];
  if (owner.hero && owner.hero.hp > 0) candidates.push(owner.hero);
  for (const u of owner.unitsOnField()) candidates.push(u);
  for (const c of candidates) {
    const fn = _onAllyHealedObservers.get(c.id);
    if (!fn) continue;
    if (c.current?.silenced) continue;
    if (c.silenceSources && c.silenceSources.size > 0) continue;
    if (c.type === 'unit' && (!c.isAlive || !c.isAlive())) continue;
    if (c.type === 'hero' && (c.hp == null || c.hp <= 0)) continue;
    try { fn(state, c, healEvent); }
    catch (e) { console.warn(`[Корпы] on_ally_healed (${c.id}) упал:`, e); }
  }
}

// В.2.g: overflow-observer dispatcher (Зейн).
function runOnHealOverflowObservers(state, healEvent) {
  if (!state || !healEvent?.target) return;
  if (_onHealOverflowObservers.size === 0) return;
  const ownerIdx = healEvent.target.ownerId;
  if (ownerIdx == null) return;
  const owner = state.players?.[ownerIdx];
  if (!owner) return;
  const candidates = [];
  if (owner.hero && owner.hero.hp > 0) candidates.push(owner.hero);
  for (const u of owner.unitsOnField()) candidates.push(u);
  for (const c of candidates) {
    const fn = _onHealOverflowObservers.get(c.id);
    if (!fn) continue;
    if (c.current?.silenced) continue;
    if (c.silenceSources && c.silenceSources.size > 0) continue;
    if (c.type === 'unit' && (!c.isAlive || !c.isAlive())) continue;
    if (c.type === 'hero' && (c.hp == null || c.hp <= 0)) continue;
    try { fn(state, c, healEvent); }
    catch (e) { console.warn(`[Корпы] on_heal_overflow (${c.id}) упал:`, e); }
  }
}

function sourceLabel(source) {
  if (!source) return 'неизвестно';
  if (typeof source === 'string') return source;
  if (typeof source === 'object') {
    if (source.type === 'hero') return shortHeroName(source);
    return source.name_ru || source.id || 'неизвестно';
  }
  return String(source);
}
